// client/src/components/company/DepositsDashboard.js
// Dashboard di dettaglio di un prodotto: Depositi (time deposit, product="allocation") o Denaro Caldo
// (product="hot_money"). Stesso layout: outstanding, interessi, tasso massimo, richieste in corso,
// posizioni in essere e totali 2026 per banca e per durata.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatDuration, formatCurrencyWithSymbol, formatInterestRate } from '../utils/formatUtils';
import { useResponsive } from '../../hooks/useResponsive';
import { TrendingUp, CalendarClock, Upload, Download, Coins, Percent, PieChart as PieChartIcon } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';

// Colori e testi per prodotto: stesse palette dei riquadri Outstanding della dashboard generale
// Titoli delle card KPI (outstanding, interessi, tasso) in blu per tutti i prodotti
const KPI_TITLE_COLOR = '#1a73e8';
const PRODUCTS = {
  allocation: {
    color: '#1a73e8',
    pieColors: ['#1a73e8', '#5c9ce6', '#2e7d32', '#78909c', '#f4a742', '#607d8b', '#8e44ad', '#95a5a6', '#3498db', '#7f8c8d'],
    trackColor: '#e8eef7',
    // Dal più scuro (durate brevi) al più chiaro (durate lunghe)
    durationColors: ['#0d47a1', '#1a73e8', '#7fb0ee', '#c9ddf8'],
    endpoint: '/api/requests/dashboard/deposits',
    titleKey: 'sidebar.allocations',
    yearTitleKey: 'dashboard.totalAllocation2026',
    yearIcon: Upload,
    newRequestPath: '/company/new-request/allocation',
    noRate3mKey: 'depositsDashboard.noRate3m',
    interestKey: 'depositsDashboard.interestYtd',
    bestRateKey: 'depositsDashboard.maxRate3m',
    yearTotalKey: 'dashboard.totalAllocated'
  },
  hot_money: {
    color: '#e65100',
    pieColors: ['#e65100', '#ffb300', '#bf360c', '#ffd54f', '#fb8c00', '#8d6e63', '#f4511e', '#fdd835', '#ffa726', '#d84315'],
    trackColor: '#fbeadf',
    durationColors: ['#bf360c', '#e65100', '#ffa726', '#ffe0b2'],
    endpoint: '/api/requests/dashboard/hot-money',
    titleKey: 'sidebar.hotMoney',
    yearTitleKey: 'dashboard.totalHotMoney2026',
    yearIcon: Download,
    newRequestPath: '/company/new-request/hot-money',
    noRate3mKey: 'depositsDashboard.noRate3mHotMoney',
    interestKey: 'depositsDashboard.interestPaidYtd',
    // Il denaro caldo lo paga l'azienda: il miglior tasso è il minimo
    bestRateKey: 'depositsDashboard.minRate3m',
    yearTotalKey: 'depositsDashboard.totalRequested'
  }
};
const pieTooltipStyle = {
  borderRadius: '8px',
  border: '1px solid rgba(11,11,11,0.10)',
  boxShadow: '0 2px 8px rgba(11,11,11,0.12)',
  fontSize: '13px',
  padding: '8px 12px'
};
const DONUT_SIZE = 170;
const DURATION_BUCKETS = [
  { key: 'lt15', labelKey: 'depositsDashboard.durationLt15' },
  { key: 'd15_30', labelKey: 'depositsDashboard.duration15_30' },
  { key: 'd31_90', labelKey: 'depositsDashboard.duration31_90' },
  { key: 'gt90', labelKey: 'depositsDashboard.durationGt90' }
];
const TOP_BANKS = 5;
const MAX_KPI_CURRENCIES = 3;

const kpiLabelStyle = { fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: '#767e8e' };
const kpiValueStyle = { fontSize: '26px', fontWeight: 'bold', lineHeight: 1.2, margin: '6px 0 4px' };
const kpiNoteStyle = { fontSize: '13px', color: '#767e8e' };
const emptyStateStyle = { textAlign: 'center', padding: '24px', color: '#666' };

const formatShare = (share) => (share > 0 && share < 1 ? '<1%' : `${Math.round(share)}%`);

const DepositsDashboard = ({ product = 'allocation' }) => {
  const { t } = useTranslation();
  // Passed to the detail page so its back button returns here
  const location = useLocation();
  const fromDashboard = { from: location.pathname };
  const { isMobile } = useResponsive();
  const { isAuthenticated, token, initialized } = useAuth();
  const {
    color: PRODUCT_COLOR, pieColors, trackColor, durationColors, endpoint,
    titleKey, yearTitleKey, yearIcon: YearIcon, newRequestPath, noRate3mKey, interestKey,
    bestRateKey, yearTotalKey
  } = PRODUCTS[product];
  const isHotMoney = product === 'hot_money';

  const [data, setData] = useState(null);
  const [openRequests, setOpenRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cardCurrencies, setCardCurrencies] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        const [dashboardRes, requestsRes] = await Promise.all([
          axios.get(endpoint),
          axios.get('/api/requests')
        ]);
        setData(dashboardRes.data);
        setOpenRequests(
          requestsRes.data.requests
            .filter(request =>
              ['bidding', 'pending_allocation'].includes(request.status) &&
              (request.requestType === 'hot_money') === isHotMoney
            )
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        );
      } catch (err) {
        console.error(`${product} dashboard fetch error:`, err);
        setError(t('common.errors.fetchDashboard', {
          defaultValue: 'Failed to fetch dashboard data. Please try again.'
        }));
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && initialized) {
      fetchData();
    }
  }, [isAuthenticated, token, initialized, t, product, endpoint, isHotMoney]);

  const getStatusTranslation = (status) => {
    switch (status) {
      case 'bidding':
        return t('status.bidding');
      case 'pending_allocation':
        return t('status.pending');
      default:
        return status.replace('_', ' ');
    }
  };

  const formatRate = (rate) => (rate ? formatInterestRate(rate.interestRate, rate.rateType, rate.baseRate) : '-');
  const formatSpread = (spread) => (spread === null || spread === undefined
    ? '-'
    : `${spread > 0 ? '+' : ''}${spread.toFixed(2)}%`);
  const formatDate = (date) => new Date(date).toLocaleDateString();

  // Stesso formato della tabella "Prossime scadenze" della dashboard generale
  const formatDaysLeft = (daysLeft) => {
    if (daysLeft <= 0) return t('dashboard.today');
    if (daysLeft === 1) return t('dashboard.inOneDay');
    return t('dashboard.inDays', { n: daysLeft });
  };

  // Currencies with data for a card: EUR first, then alphabetical
  const sortCurrencies = (currencies) => [...currencies].sort((a, b) => {
    if (a === 'EUR') return -1;
    if (b === 'EUR') return 1;
    return a.localeCompare(b);
  });

  // Card currency: the one picked by the user, otherwise EUR (or the only currency with data)
  const resolveCardCurrency = (cardKey, currencies) => {
    const chosen = cardCurrencies[cardKey];
    if (chosen && currencies.includes(chosen)) return chosen;
    return currencies.length === 0 || currencies.includes('EUR') ? 'EUR' : currencies[0];
  };

  // Selector in the card, only when the card has data in more than one currency
  const renderCardCurrencySelector = (cardKey, currencies, value) => (
    currencies.length > 1 && (
      <select
        aria-label={t('dashboard.currency')}
        value={value}
        onChange={(e) => setCardCurrencies(prev => ({ ...prev, [cardKey]: e.target.value }))}
        style={{ width: 'auto', marginLeft: 'auto', padding: '4px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', fontWeight: 500, cursor: 'pointer', backgroundColor: '#fff' }}
      >
        {currencies.map(cur => (
          <option key={cur} value={cur}>{cur}</option>
        ))}
      </select>
    )
  );

  // Barra di intestazione blu, come il riquadro Outstanding
  const renderKpiHeader = (Icon, label) => (
    <div className="card-title">
      <Icon className="card-title-icon" size={20} style={{ color: KPI_TITLE_COLOR }} />
      <span style={{ color: KPI_TITLE_COLOR }}>{label}</span>
    </div>
  );

  // KPI cards without a currency selector: all the currencies used, up to MAX_KPI_CURRENCIES (EUR first)
  const getKpiCurrencies = (byCurrency) => sortCurrencies(Object.keys(byCurrency)).slice(0, MAX_KPI_CURRENCIES);

  // One row per currency when the card shows more than one
  const kpiRowStyle = { display: 'flex', alignItems: 'baseline', gap: '12px', padding: '6px 0', borderBottom: '1px solid #f0f2f5' };
  const kpiRowValueStyle = { fontSize: '22px', fontWeight: 'bold', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' };
  const kpiRowCurrencyStyle = { ...kpiLabelStyle, width: '34px', flexShrink: 0 };

  // ── KPI: outstanding per banca, identico al riquadro della dashboard generale ──
  const renderOutstandingCard = () => {
    const byCurrency = data?.outstanding || {};
    const currencies = sortCurrencies(Object.keys(byCurrency));
    const currency = resolveCardCurrency('outstanding', currencies);
    const outstanding = byCurrency[currency] || { total: 0, count: 0, byBank: [] };
    const bankColor = (index) => pieColors[index % pieColors.length];

    return (
      <div className="card" style={{ marginBottom: 0 }}>
        <div className="card-title">
          <PieChartIcon className="card-title-icon" size={20} style={{ color: KPI_TITLE_COLOR }} />
          <span style={{ color: KPI_TITLE_COLOR }}>
            {isHotMoney ? t('dashboard.hotMoneyOutstanding') : `${t(titleKey)} ${t('dashboard.outstanding')}`}
          </span>
          {renderCardCurrencySelector('outstanding', currencies, currency)}
        </div>

        {outstanding.byBank.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {/* Donut with the number of outstanding positions in the middle */}
            <div style={{ position: 'relative', width: DONUT_SIZE, height: DONUT_SIZE, flexShrink: 0, margin: isMobile ? '0 auto' : 0 }}>
              <PieChart width={DONUT_SIZE} height={DONUT_SIZE}>
                <Pie
                  data={outstanding.byBank}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={58}
                  outerRadius={82}
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={outstanding.byBank.length > 1 ? 2 : 0}
                  cornerRadius={3}
                  stroke="none"
                >
                  {outstanding.byBank.map((entry, index) => (
                    <Cell key={`cell-${entry.name}`} fill={bankColor(index)} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => formatCurrencyWithSymbol(value, currency)}
                  contentStyle={pieTooltipStyle}
                  wrapperStyle={{ zIndex: 10 }}
                />
              </PieChart>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <div style={{ fontSize: '26px', fontWeight: 700, color: '#1f2937', lineHeight: 1.1 }}>{outstanding.count}</div>
                <div style={{ fontSize: '11px', color: '#767e8e', textAlign: 'center', maxWidth: '90px' }}>
                  {t('dashboard.positionsOutstanding')}
                </div>
              </div>
            </div>

            {/* Right column: outstanding total, then legend with bank, amount and share */}
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ fontSize: '26px', fontWeight: 'bold', color: PRODUCT_COLOR, lineHeight: 1.2, marginBottom: '6px' }}>
                {formatCurrencyWithSymbol(Math.round(outstanding.total), currency)}
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: DONUT_SIZE - 50, overflowY: 'auto' }}>
                {outstanding.byBank.map((bank, index) => {
                  const share = outstanding.total > 0 ? (bank.value / outstanding.total) * 100 : 0;
                  return (
                    <li
                      key={bank.name}
                      style={{ display: 'grid', gridTemplateColumns: '10px minmax(0, 1fr) auto 44px', alignItems: 'center', gap: '10px', padding: '7px 0', borderBottom: '1px solid #f0f2f5', fontSize: '13px' }}
                    >
                      <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: bankColor(index) }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1f2937' }} title={bank.name}>
                        {bank.name}
                      </span>
                      <span style={{ fontWeight: 600, color: '#1f2937', fontVariantNumeric: 'tabular-nums' }}>
                        {formatCurrencyWithSymbol(Math.round(bank.value), currency)}
                      </span>
                      <span style={{ textAlign: 'right', color: '#767e8e', fontVariantNumeric: 'tabular-nums' }}>
                        {formatShare(share)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ) : (
          <div style={emptyStateStyle}>{t('dashboard.noOutstanding')}</div>
        )}
      </div>
    );
  };

  // ── KPI: interessi maturati da inizio anno ──
  const renderInterestCard = () => {
    const byCurrency = data?.interestYtd || {};
    const currencies = getKpiCurrencies(byCurrency);

    return (
      <div className="card" style={{ marginBottom: 0 }}>
        {renderKpiHeader(Coins, t(interestKey))}
        {currencies.length > 1 ? (
          <div style={{ margin: '4px 0 8px' }}>
            {currencies.map(currency => (
              <div key={currency} style={kpiRowStyle}>
                <span style={kpiRowCurrencyStyle}>{currency}</span>
                <span style={{ ...kpiRowValueStyle, color: PRODUCT_COLOR }}>
                  {formatCurrencyWithSymbol(Math.round(byCurrency[currency] || 0), currency)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ ...kpiValueStyle, color: PRODUCT_COLOR }}>
            {formatCurrencyWithSymbol(Math.round(byCurrency[currencies[0]] || 0), currencies[0] || 'EUR')}
          </div>
        )}
        <div style={kpiNoteStyle}>{t('depositsDashboard.sinceStartOfYear')}</div>
      </div>
    );
  };

  // ── KPI: miglior tasso degli ultimi 3 mesi (massimo per i depositi, minimo per il denaro caldo) e banca ──
  const renderBestRateCard = () => {
    const byCurrency = data?.bestRate3m || {};
    const currencies = getKpiCurrencies(byCurrency);
    const bestRate = byCurrency[currencies[0]];

    return (
      <div className="card" style={{ marginBottom: 0 }}>
        {renderKpiHeader(Percent, t(bestRateKey))}
        {currencies.length > 1 ? (
          <div style={{ margin: '4px 0 0' }}>
            {currencies.map(currency => {
              const rate = byCurrency[currency];
              return (
                <div key={currency} style={kpiRowStyle}>
                  <span style={kpiRowCurrencyStyle}>{currency}</span>
                  <span style={{ ...kpiRowValueStyle, color: PRODUCT_COLOR }}>{`${rate.effectiveRate.toFixed(2)}%`}</span>
                  <span style={{ ...kpiNoteStyle, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={rate.rateType === 'floating' ? `${rate.bankName} · ${formatRate(rate)}` : rate.bankName}>
                    {rate.bankName}
                    {rate.rateType === 'floating' && <span style={{ fontSize: '12px' }}> · {formatRate(rate)}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        ) : bestRate ? (
          <>
            <div style={{ ...kpiValueStyle, fontSize: '32px', color: PRODUCT_COLOR }}>{`${bestRate.effectiveRate.toFixed(2)}%`}</div>
            <div style={kpiNoteStyle}>{t('depositsDashboard.bankName', { name: bestRate.bankName })}</div>
            {bestRate.rateType === 'floating' && (
              <div style={{ ...kpiNoteStyle, fontSize: '12px', marginTop: '4px' }}>{formatRate(bestRate)}</div>
            )}
          </>
        ) : (
          <div style={{ ...kpiNoteStyle, marginTop: '10px' }}>{t(noRate3mKey)}</div>
        )}
      </div>
    );
  };

  // ── Richieste time deposit in corso (bidding + pending_allocation) ──
  const renderOpenRequests = () => (
    <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
      <div className="card-title">
        <TrendingUp className="card-title-icon" size={20} />
        <span>{t('depositsDashboard.openRequests')}</span>
      </div>

      {!isMobile && (
        <table className="requests-list-table">
          <thead>
            <tr>
              <th>{t('requests.requestId')}</th>
              <th>{t('requests.dateCreated')}</th>
              <th>{t('requests.amount')}</th>
              <th>{t('bids.bidsReceived')}</th>
              <th>{t('requests.maturity')}</th>
              <th>{t('bids.status')}</th>
              <th>{t('common.action')}</th>
            </tr>
          </thead>
          <tbody>
            {openRequests.length > 0 ? (
              openRequests.map(request => (
                <tr key={request.id}>
                  <td>#{request.requestId}</td>
                  <td>{formatDate(request.createdAt)}</td>
                  <td>{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</td>
                  <td>{formatCurrencyWithSymbol(request.totalActiveBidAmount, request.currency || 'EUR')}</td>
                  <td>{formatDuration(request.duration, t, request.customStartDate, request.customEndDate)}</td>
                  <td>
                    <span className={`status status-${request.status}`}>{getStatusTranslation(request.status)}</span>
                  </td>
                  <td>
                    <Link to={`/company/requests/${request.id}`} state={fromDashboard} className="view-btn">{t('common.view')}</Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7}>{t('depositsDashboard.noOpenRequests')}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {isMobile && (
        <div className="mobile-cards-container">
          {openRequests.length > 0 ? (
            openRequests.map(request => (
              <div className="mobile-record-card" key={request.id}>
                <div className="mobile-card-header">
                  <div className="mobile-card-title">#{request.requestId}</div>
                  <div className="mobile-card-status">
                    <span className={`status status-${request.status}`}>{getStatusTranslation(request.status)}</span>
                  </div>
                </div>
                <div className="mobile-card-body">
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">{t('requests.dateCreated')}</span>
                    <span className="mobile-card-value">{formatDate(request.createdAt)}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">{t('requests.amount')}</span>
                    <span className="mobile-card-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">{t('bids.bidsReceived')}</span>
                    <span className="mobile-card-value">{formatCurrencyWithSymbol(request.totalActiveBidAmount, request.currency || 'EUR')}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">{t('requests.maturity')}</span>
                    <span className="mobile-card-value">{formatDuration(request.duration, t, request.customStartDate, request.customEndDate)}</span>
                  </div>
                </div>
                <div className="mobile-card-footer">
                  <div className="mobile-card-date" />
                  <div className="mobile-card-action">
                    <Link to={`/company/requests/${request.id}`} state={fromDashboard} className="view-btn">{t('common.view')}</Link>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="mobile-record-card mobile-empty-card">{t('depositsDashboard.noOpenRequests')}</div>
          )}
        </div>
      )}
    </div>
  );

  // ── Prossime scadenze dei depositi attivi ──
  const renderOutstandingDeposits = () => {
    const deposits = data?.deposits || [];

    return (
      <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
        <div className="card-title">
          <CalendarClock className="card-title-icon" size={20} />
          <span>{t('dashboard.upcomingMaturities')}</span>
        </div>

        {!isMobile && (
          <table className="requests-list-table">
            <thead>
              <tr>
                <th>{t('requests.requestId')}</th>
                <th>{t('dashboard.maturityDate')}</th>
                <th>{t('dashboard.daysLeft')}</th>
                <th>{t('dashboard.bank')}</th>
                <th>{t('requests.amount')}</th>
                <th>{t('dashboard.rate')}</th>
                <th>{t('depositsDashboard.valueDate')}</th>
                <th title={t('depositsDashboard.spreadInfo')}>{t('depositsDashboard.spread')}</th>
                <th>{t('depositsDashboard.expectedInterest')}</th>
              </tr>
            </thead>
            <tbody>
              {deposits.length > 0 ? (
                deposits.map((deposit, index) => (
                  <tr key={`${deposit.requestMongoId}-${deposit.bankName}-${index}`}>
                    <td>
                      <Link to={`/company/requests/${deposit.requestMongoId}`} state={fromDashboard} className="view-btn">#{deposit.requestId}</Link>
                    </td>
                    <td><strong>{formatDate(deposit.endDate)}</strong></td>
                    <td>{formatDaysLeft(deposit.daysLeft)}</td>
                    <td>{deposit.bankName}</td>
                    <td>{formatCurrencyWithSymbol(deposit.amount, deposit.currency)}</td>
                    <td>{formatRate(deposit)}</td>
                    <td>{formatDate(deposit.valueDate)}</td>
                    <td style={{ textAlign: 'center' }}>{formatSpread(deposit.spread)}</td>
                    <td>
                      {deposit.expectedInterest !== null
                        ? formatCurrencyWithSymbol(Math.round(deposit.expectedInterest), deposit.currency)
                        : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9}>{t('dashboard.noMaturities')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {isMobile && (
          <div className="mobile-cards-container">
            {deposits.length > 0 ? (
              deposits.map((deposit, index) => (
                <div className="mobile-record-card" key={`${deposit.requestMongoId}-${deposit.bankName}-${index}`}>
                  <div className="mobile-card-header">
                    <div className="mobile-card-title">
                      <Link to={`/company/requests/${deposit.requestMongoId}`} state={fromDashboard} className="view-btn">#{deposit.requestId}</Link>
                    </div>
                    <div className="mobile-card-status">{formatDate(deposit.endDate)}</div>
                  </div>
                  <div className="mobile-card-body">
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">{t('dashboard.daysLeft')}</span>
                      <span className="mobile-card-value">{formatDaysLeft(deposit.daysLeft)}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">{t('dashboard.bank')}</span>
                      <span className="mobile-card-value">{deposit.bankName}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">{t('requests.amount')}</span>
                      <span className="mobile-card-value">{formatCurrencyWithSymbol(deposit.amount, deposit.currency)}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">{t('dashboard.rate')}</span>
                      <span className="mobile-card-value">{formatRate(deposit)}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">{t('depositsDashboard.valueDate')}</span>
                      <span className="mobile-card-value">{formatDate(deposit.valueDate)}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">{t('depositsDashboard.spread')}</span>
                      <span className="mobile-card-value">{formatSpread(deposit.spread)}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">{t('depositsDashboard.expectedInterest')}</span>
                      <span className="mobile-card-value">
                        {deposit.expectedInterest !== null
                          ? formatCurrencyWithSymbol(Math.round(deposit.expectedInterest), deposit.currency)
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="mobile-record-card mobile-empty-card">{t('dashboard.noMaturities')}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Depositi 2026: totali a sinistra, suddivisione per banca e per durata a destra ──
  const renderYearCard = () => {
    const totals = data?.totals || { activeRequests: 0, byCurrency: {} };
    const currencies = sortCurrencies(Object.keys(totals.byCurrency));
    const currency = resolveCardCurrency('year', currencies);
    const year = totals.byCurrency[currency] || {
      totalAllocated: 0,
      lastRate: null,
      byBank: [],
      byDuration: { lt15: 0, d15_30: 0, d31_90: 0, gt90: 0 }
    };
    const total = year.totalAllocated;
    const shareOf = (value) => (total > 0 ? (value / total) * 100 : 0);

    // Prime 5 banche, le altre raggruppate
    const topBanks = year.byBank.slice(0, TOP_BANKS);
    const otherBanks = year.byBank.slice(TOP_BANKS);
    const bankRows = otherBanks.length > 0
      ? [...topBanks, {
          name: t('depositsDashboard.otherBanks', { count: otherBanks.length }),
          value: otherBanks.reduce((sum, bank) => sum + bank.value, 0)
        }]
      : topBanks;

    const sectionBorder = isMobile
      ? { borderTop: '1px solid #eef1f6', paddingTop: '16px' }
      : { borderLeft: '1px solid #eef1f6', paddingLeft: '24px' };

    return (
      <div className="card">
        <div className="card-title">
          <YearIcon className="card-title-icon" size={20} />
          <span>{t(yearTitleKey)}</span>
          {renderCardCurrencySelector('year', currencies, currency)}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(200px, 0.8fr) minmax(0, 1.4fr) minmax(0, 1.4fr)', gap: '24px' }}>
          {/* Totali (come nella dashboard generale) */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={kpiLabelStyle}>{t(yearTotalKey)}</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: PRODUCT_COLOR, marginBottom: '16px' }}>
              {formatCurrencyWithSymbol(Math.round(total), currency)}
            </div>
            <div style={{ display: 'flex', gap: '32px', marginBottom: '20px' }}>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>{totals.activeRequests}</div>
                <div style={{ fontSize: '12px', color: '#767e8e' }}>{t('dashboard.activeRequestsCount')}</div>
              </div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>{formatRate(year.lastRate)}</div>
                <div style={{ fontSize: '12px', color: '#767e8e' }}>{t('dashboard.lastRate')}</div>
              </div>
            </div>
            <div style={{ marginTop: 'auto' }}>
              <Link to={newRequestPath} className="btn btn-primary">{t('sidebar.newRequest')}</Link>
            </div>
          </div>

          {/* Per banca */}
          <div style={sectionBorder}>
            <div style={{ ...kpiLabelStyle, marginBottom: '12px' }}>{t('depositsDashboard.byBank')}</div>
            {bankRows.length > 0 ? (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {bankRows.map(bank => {
                  const share = shareOf(bank.value);
                  return (
                    <li
                      key={bank.name}
                      title={formatCurrencyWithSymbol(Math.round(bank.value), currency)}
                      style={{ display: 'grid', gridTemplateColumns: 'minmax(80px, 120px) minmax(0, 1fr) 40px', alignItems: 'center', gap: '10px', marginBottom: '9px', fontSize: '13px' }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1f2937' }}>{bank.name}</span>
                      <span style={{ height: '8px', borderRadius: '4px', backgroundColor: trackColor, overflow: 'hidden' }}>
                        <span style={{ display: 'block', height: '100%', width: `${share}%`, backgroundColor: PRODUCT_COLOR, borderRadius: '4px' }} />
                      </span>
                      <span style={{ textAlign: 'right', fontWeight: 600, color: '#1f2937', fontVariantNumeric: 'tabular-nums' }}>{formatShare(share)}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div style={emptyStateStyle}>{t('dashboard.noAllocations2026')}</div>
            )}
          </div>

          {/* Per durata */}
          <div style={sectionBorder}>
            <div style={{ ...kpiLabelStyle, marginBottom: '12px' }}>{t('depositsDashboard.byDuration')}</div>
            {total > 0 ? (
              <>
                <div style={{ display: 'flex', height: '14px', borderRadius: '4px', overflow: 'hidden', backgroundColor: trackColor, marginBottom: '16px' }}>
                  {DURATION_BUCKETS.map((bucket, index) => (
                    <span
                      key={bucket.key}
                      title={`${t(bucket.labelKey)}: ${formatShare(shareOf(year.byDuration[bucket.key]))}`}
                      style={{ width: `${shareOf(year.byDuration[bucket.key])}%`, backgroundColor: durationColors[index] }}
                    />
                  ))}
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {DURATION_BUCKETS.map((bucket, index) => (
                    <li
                      key={bucket.key}
                      title={formatCurrencyWithSymbol(Math.round(year.byDuration[bucket.key]), currency)}
                      style={{ display: 'grid', gridTemplateColumns: '10px minmax(0, 1fr) 40px', alignItems: 'center', gap: '10px', marginBottom: '9px', fontSize: '13px' }}
                    >
                      <span style={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: durationColors[index] }} />
                      <span style={{ color: '#1f2937' }}>{t(bucket.labelKey)}</span>
                      <span style={{ textAlign: 'right', fontWeight: 600, color: '#1f2937', fontVariantNumeric: 'tabular-nums' }}>
                        {formatShare(shareOf(year.byDuration[bucket.key]))}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div style={emptyStateStyle}>{t('dashboard.noAllocations2026')}</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">{t('common.loadingDashboard')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="company-dashboard">
      {/* KPI: outstanding, interessi maturati, tasso massimo */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)', gap: '20px', marginBottom: '20px' }}>
        {renderOutstandingCard()}
        {renderInterestCard()}
        {renderBestRateCard()}
      </div>

      {renderOpenRequests()}
      {renderOutstandingDeposits()}
      {renderYearCard()}
    </div>
  );
};

export default DepositsDashboard;
